import { useState, useCallback, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Stack,
	Typography,
	Button,
	IconButton,
	Avatar,
	Chip,
	Divider,
	Alert,
	Tooltip,
	CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import UndoIcon from "@mui/icons-material/Undo";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { generateButtonSx } from "../../../utils/elementor-style-override";

// ─── Constants ────────────────────────────────────────────────────────────────

const LEVEL_CONFIG = {
	L1: { label: "Level 1", color: "#006064", bg: "#E0F7FA", accent: "#00ACC1", border: "#80DEEA" },
	L2: { label: "Level 2", color: "#1565C0", bg: "#E3F2FD", accent: "#1976D2", border: "#90CAF9" },
	L3: { label: "Level 3", color: "#4527A0", bg: "#EDE7F6", accent: "#7E57C2", border: "#CE93D8" },
	UNASSIGNED: { label: "Unassigned", color: "#546E7A", bg: "#ECEFF1", accent: "#90A4AE", border: "#CFD8DC" },
};

const MAX_L1 = 10;

const getInitials = (name) =>
	name
		.split(" ")
		.map((n) => n[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

// ─── Build flat list from real network response ───────────────────────────────

const buildHierarchyState = (network) => {
	if (!network) return { l1: [], l2: [], l3: [], unassigned: [] };

	const makeAgent = (member, level, parentId = null) => ({
		id: member.ID,
		name: member.name,
		level,
		parentId,
		activeMembers: member.active_members ?? 0,
		qualification_status: member.qualification_status,
		license_status: member.license_status,
		// An anonymised opted-out agent. It only exists to keep its downlines
		// attached, so it can be neither dragged nor picked as someone's new parent —
		// reassigning against an identity we deliberately hid is not a real choice.
		isPlaceholder: member.is_placeholder === true,
	});

	return {
		l1: (network.l1 ?? []).map((m) => makeAgent(m, "L1", null)),
		l2: (network.l2 ?? []).map((m) => makeAgent(m, "L2", m.source_agent ?? null)),
		l3: (network.l3 ?? []).map((m) => makeAgent(m, "L3", m.source_agent ?? null)),
		unassigned: [],
	};
};

// ─── Diff helpers ─────────────────────────────────────────────────────────────

const flattenHierarchy = (state) => {
	const map = {};
	["l1", "l2", "l3", "unassigned"].forEach((key) => {
		state[key].forEach((agent) => {
			map[agent.id] = { level: agent.level, parentId: agent.parentId ?? null };
		});
	});
	return map;
};

const computeMoves = (original, current, currentAgentID) => {
	if (!original) return [];

	const getParentId = (node) => {
		if (!node || node.level === "UNASSIGNED") return null;
		if (node.level === "L1") return currentAgentID ?? null;
		return node.parentId ?? null;
	};

	const originalMap = flattenHierarchy(original);
	const currentMap = flattenHierarchy(current);

	return Object.entries(currentMap)
		.filter(([id, curr]) => {
			const orig = originalMap[id];
			return !orig || getParentId(orig) !== getParentId(curr);
		})
		.map(([id, curr]) => ({
			agent_id: Number(id),
			new_parent_id: getParentId(curr),
		}));
};

// ─── Drag state singleton (avoids stale closure issues) ───────────────────────

let dragPayload = null;

// ─── Agent Card (draggable) ───────────────────────────────────────────────────

const AgentCard = ({ agent }) => {
	const cfg = LEVEL_CONFIG[agent.level] ?? LEVEL_CONFIG.UNASSIGNED;
	const isPlaceholder = agent.isPlaceholder === true;

	return (
		<Box
			draggable={!isPlaceholder}
			onDragStart={(e) => {
				if (isPlaceholder) {
					e.preventDefault();
					return;
				}
				dragPayload = { agent };
				e.dataTransfer.effectAllowed = "move";
			}}
			onDragEnd={() => {
				dragPayload = null;
			}}
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.25,
				py: 1,
				borderRadius: "8px",
				border: `1.5px solid #E0E0E0`,
				backgroundColor: isPlaceholder ? "#F2F4F6" : "#FAFBFC",
				cursor: isPlaceholder ? "not-allowed" : "grab",
				opacity: isPlaceholder ? 0.65 : 1,
				transition: "all 0.12s",
				userSelect: "none",
				"&:hover": isPlaceholder ? {} : { borderColor: cfg.accent, backgroundColor: cfg.bg },
				"&:active": { cursor: isPlaceholder ? "not-allowed" : "grabbing" },
			}}
		>
			<DragIndicatorIcon sx={{ fontSize: 14, color: isPlaceholder ? "#CFD8DC" : "#B0BEC5", flexShrink: 0 }} />
			<Avatar sx={{ width: 28, height: 28, fontSize: 10, fontWeight: 700, bgcolor: isPlaceholder ? "#B0BEC5" : cfg.accent, flexShrink: 0 }}>
				{isPlaceholder ? <PersonOffIcon sx={{ fontSize: 14 }} /> : getInitials(agent.name)}
			</Avatar>
			<Box flex={1} minWidth={0}>
				<Typography
					fontSize={12}
					fontWeight={700}
					noWrap
					color={isPlaceholder ? "text.disabled" : "text.primary"}
					sx={{ fontStyle: isPlaceholder ? "italic" : "normal" }}
				>
					{agent.name}
				</Typography>
				<Typography fontSize={10} color="text.secondary">
					{isPlaceholder ? "cannot be moved" : `${agent.activeMembers} members`}
				</Typography>
			</Box>
			<Chip
				label={agent.level}
				size="small"
				sx={{
					height: 16,
					fontSize: 10,
					fontWeight: 700,
					bgcolor: isPlaceholder ? "#ECEFF1" : cfg.bg,
					color: isPlaceholder ? "text.disabled" : cfg.color,
					"& .MuiChip-label": { px: 0.75 },
					flexShrink: 0,
				}}
			/>
		</Box>
	);
};

// ─── Drop Zone ────────────────────────────────────────────────────────────────

const DropZone = ({ level, agents, onDrop, onRemoveFromZone, parentOptions, onParentChange, disabled = false }) => {
	const [isOver, setIsOver] = useState(false);
	const cfg = LEVEL_CONFIG[level] ?? LEVEL_CONFIG.UNASSIGNED;
	const isUnassigned = level === "UNASSIGNED";

	const handleDragOver = (e) => {
		if (disabled) return;
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
		setIsOver(true);
	};
	const handleDragLeave = () => setIsOver(false);
	const handleDrop = (e) => {
		e.preventDefault();
		setIsOver(false);
		if (!disabled && dragPayload) onDrop(dragPayload.agent, level);
	};

	return (
		<Box
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			sx={{
				flex: 1,
				minWidth: 0,
				border: isOver ? `2px dashed ${cfg.accent}` : "1.5px dashed #E0E0E0",
				borderRadius: "12px",
				backgroundColor: isOver ? `${cfg.bg}66` : "#FAFBFC",
				transition: "all 0.15s",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				opacity: disabled ? 0.6 : 1,
				pointerEvents: disabled ? "none" : "auto",
			}}
		>
			{/* Header */}
			<Box sx={{ px: 1.5, py: 1.25, background: isUnassigned ? "#ECEFF1" : cfg.bg, borderBottom: `1px solid ${cfg.border}` }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Stack direction="row" alignItems="center" spacing={0.75}>
						<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.accent }} />
						<Typography fontSize={12} fontWeight={700} color={cfg.color}>
							{isUnassigned ? "Unassigned" : cfg.label}
						</Typography>
					</Stack>
					<Chip
						label={agents.length}
						size="small"
						sx={{
							height: 18,
							fontSize: 10,
							fontWeight: 700,
							bgcolor: "rgba(0,0,0,0.08)",
							color: cfg.color,
							"& .MuiChip-label": { px: 0.75 },
						}}
					/>
				</Stack>
				{!isUnassigned && (
					<Typography fontSize={10} color="text.secondary" mt={0.25}>
						Drag agents here to assign {cfg.label}
					</Typography>
				)}
			</Box>

			{/* Agent list */}
			<Box sx={{ p: 1, flex: 1, overflowY: "auto", maxHeight: 340, minHeight: 80 }}>
				{agents.length === 0 ? (
					<Stack alignItems="center" justifyContent="center" py={3} spacing={0.5}>
						<AccountTreeIcon sx={{ fontSize: 22, color: "#CFD8DC" }} />
						<Typography fontSize={11} color="text.disabled" textAlign="center">
							{isUnassigned ? "All agents assigned" : "Drop agents here"}
						</Typography>
					</Stack>
				) : (
					<Stack spacing={0.75}>
						{agents.map((agent) => (
							<Box key={agent.id}>
								<AgentCard agent={agent} />

								{/* Parent selector for L2/L3 — a placeholder cannot be reassigned. */}
								{!agent.isPlaceholder && (level === "L2" || level === "L3") && parentOptions.length > 0 && (
									<Stack direction="row" alignItems="center" spacing={0.5} mt={0.5} pl={1} pr={0.5}>
										<Typography fontSize={10} color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
											Reports to:
										</Typography>
										<Box
											component="select"
											value={agent.parentId ?? ""}
											onChange={(e) => onParentChange(agent.id, Number(e.target.value) || null)}
											sx={{
												flex: 1,
												fontSize: 10,
												border: "1px solid #E0E0E0",
												borderRadius: "4px",
												px: 0.5,
												py: 0.25,
												bgcolor: "#fff",
												color: "text.primary",
												cursor: "pointer",
												"&:focus": { outline: `2px solid ${cfg.accent}`, outlineOffset: 1 },
											}}
										>
											<option value="">— Select parent —</option>
											{/* Placeholders stay listed but disabled: an agent may already
											    report to one, so the option has to exist for the current
											    value to render — it just can't be chosen for anyone new. */}
											{parentOptions.map((p) => (
												<option key={p.id} value={p.id} disabled={p.isPlaceholder === true}>
													{p.name}
												</option>
											))}
										</Box>
										<Tooltip title="Remove from hierarchy">
											<IconButton
												size="small"
												onClick={() => onRemoveFromZone(agent.id)}
												sx={{
													p: 0.25,
													color: "#B0BEC5",
													"&:hover": { color: "#D32F2F" },
													backgroundColor: "transparent !important",
												}}
											>
												<PersonRemoveIcon sx={{ fontSize: 14 }} />
											</IconButton>
										</Tooltip>
									</Stack>
								)}

								{/* Remove button for L1 */}
								{level === "L1" && (
									<Stack direction="row" justifyContent="flex-end" mt={0.25} pr={0.5}>
										<Tooltip title="Remove from hierarchy">
											<IconButton
												size="small"
												onClick={() => onRemoveFromZone(agent.id)}
												sx={{
													p: 0.25,
													color: "#B0BEC5",
													"&:hover": { color: "#D32F2F" },
													backgroundColor: "transparent !important",
												}}
											>
												<PersonRemoveIcon sx={{ fontSize: 14 }} />
											</IconButton>
										</Tooltip>
									</Stack>
								)}
							</Box>
						))}
					</Stack>
				)}
			</Box>

			{/* Drop hint */}
			{isOver && (
				<Box sx={{ px: 1.5, py: 0.75, backgroundColor: cfg.bg, borderTop: `1px dashed ${cfg.accent}` }}>
					<Typography fontSize={11} color={cfg.color} fontWeight={600} textAlign="center">
						↓ Drop to assign {isUnassigned ? "as Unassigned" : cfg.label}
					</Typography>
				</Box>
			)}
		</Box>
	);
};

// ─── Validation ───────────────────────────────────────────────────────────────

const validateHierarchy = (state) => {
	const errors = [];
	const warnings = [];

	state.l2.forEach((a) => {
		if (!a.parentId) errors.push(`${a.name} (L2) has no L1 parent assigned.`);
	});
	state.l3.forEach((a) => {
		if (!a.parentId) errors.push(`${a.name} (L3) has no L2 parent assigned.`);
	});
	if (state.l1.length > MAX_L1) warnings.push(`You have ${state.l1.length} direct L1 agents. Consider a max of ${MAX_L1}.`);
	if (state.unassigned.length > 0) warnings.push(`${state.unassigned.length} agent(s) are currently unassigned.`);

	return { errors, warnings, valid: errors.length === 0 };
};

// ─── Change log row ───────────────────────────────────────────────────────────

const ChangeRow = ({ change }) => (
	<Stack direction="row" alignItems="flex-start" spacing={1} py={0.75}>
		<SwapVertIcon sx={{ fontSize: 14, color: "#90A4AE", mt: 0.25, flexShrink: 0 }} />
		<Typography fontSize={11} color="text.secondary">
			<strong style={{ color: "#1a2332" }}>{change.name}</strong> moved from{" "}
			<Chip
				label={change.from}
				size="small"
				sx={{
					height: 14,
					fontSize: 9,
					fontWeight: 700,
					bgcolor: (LEVEL_CONFIG[change.from] ?? LEVEL_CONFIG.UNASSIGNED).bg,
					color: (LEVEL_CONFIG[change.from] ?? LEVEL_CONFIG.UNASSIGNED).color,
					"& .MuiChip-label": { px: 0.5 },
				}}
			/>
			{" → "}
			<Chip
				label={change.to}
				size="small"
				sx={{
					height: 14,
					fontSize: 9,
					fontWeight: 700,
					bgcolor: (LEVEL_CONFIG[change.to] ?? LEVEL_CONFIG.UNASSIGNED).bg,
					color: (LEVEL_CONFIG[change.to] ?? LEVEL_CONFIG.UNASSIGNED).color,
					"& .MuiChip-label": { px: 0.5 },
				}}
			/>
		</Typography>
	</Stack>
);

// ─── Main Dialog ──────────────────────────────────────────────────────────────

export default function HierarchyRearrangeDialog({ open, onClose, currentAgent, network, onSave, saving = false }) {
	const [hierarchy, setHierarchy] = useState(() => buildHierarchyState(network));
	const [originalHierarchy, setOriginalHierarchy] = useState(null);
	const [changeLog, setChangeLog] = useState([]);
	const [history, setHistory] = useState([]);
	const [saved, setSaved] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const isBusy = saving || submitting;

	useEffect(() => {
		if (open) {
			const initial = buildHierarchyState(network);
			setHierarchy(initial);
			setOriginalHierarchy(initial);
			setChangeLog([]);
			setHistory([]);
			setSaved(false);
			setSubmitting(false);
		}
	}, [open, network]);

	const pushHistory = useCallback((prev) => {
		setHistory((h) => [...h.slice(-19), JSON.parse(JSON.stringify(prev))]);
	}, []);

	const handleUndo = useCallback(() => {
		if (history.length === 0) return;
		setHistory((h) => h.slice(0, -1));
		setHierarchy(history[history.length - 1]);
		setChangeLog((log) => log.slice(0, -1));
	}, [history]);

	const handleDrop = useCallback(
		(agent, targetLevel) => {
			if (agent.level === targetLevel) return;
			pushHistory(hierarchy);

			setHierarchy((prev) => {
				const next = { l1: [...prev.l1], l2: [...prev.l2], l3: [...prev.l3], unassigned: [...prev.unassigned] };
				const fromKey = agent.level.toLowerCase();
				const targetKey = targetLevel.toLowerCase();
				next[fromKey] = next[fromKey].filter((a) => a.id !== agent.id);
				next[targetKey] = [
					...next[targetKey],
					{
						...agent,
						level: targetLevel,
						parentId: targetLevel === "L1" ? (currentAgent?.ID ?? null) : null,
					},
				];
				return next;
			});

			setChangeLog((log) => [...log, { name: agent.name, from: agent.level, to: targetLevel, agentId: agent.id }]);
		},
		[hierarchy, pushHistory, currentAgent?.ID]
	);

	const handleRemove = useCallback(
		(agentId, fromLevel) => {
			const fromKey = fromLevel.toLowerCase();
			const agent = hierarchy[fromKey]?.find((a) => a.id === agentId);
			if (!agent) return;

			pushHistory(hierarchy);
			setHierarchy((prev) => ({
				...prev,
				[fromKey]: prev[fromKey].filter((a) => a.id !== agentId),
				unassigned: [...prev.unassigned, { ...agent, level: "UNASSIGNED", parentId: null }],
			}));
			setChangeLog((log) => [...log, { name: agent.name, from: fromLevel, to: "UNASSIGNED", agentId }]);
		},
		[hierarchy, pushHistory]
	);

	const handleParentChange = useCallback((agentId, newParentId, level) => {
		const key = level.toLowerCase();
		setHierarchy((prev) => ({
			...prev,
			[key]: prev[key].map((a) => (a.id === agentId ? { ...a, parentId: newParentId } : a)),
		}));
	}, []);

	const handleSave = async () => {
		const { valid } = validateHierarchy(hierarchy);
		if (!valid || isBusy) return;

		const moves = computeMoves(originalHierarchy, hierarchy, currentAgent?.ID);
		if (moves.length === 0) {
			onClose();
			return;
		}

		setSubmitting(true);
		try {
			await onSave?.(moves);
			setSaved(true);
		} catch (err) {
			console.error(err);
		} finally {
			setSubmitting(false);
		}
	};

	const { errors, warnings, valid } = validateHierarchy(hierarchy);
	const hasChanges = changeLog.length > 0;

	return (
		<Dialog
			open={open}
			onClose={isBusy ? undefined : onClose}
			maxWidth="lg"
			fullWidth
			PaperProps={{ sx: { borderRadius: "16px", maxHeight: "92vh", bgcolor: "#F8FAFC" } }}
		>
			{/* Title bar */}
			<DialogTitle
				sx={{
					background: "linear-gradient(135deg, #1D54A2 0%, #0f3875 100%)",
					color: "#fff",
					py: 2,
					px: 3,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<Box
						sx={{
							width: 36,
							height: 36,
							borderRadius: "10px",
							bgcolor: "rgba(255,255,255,0.15)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<AccountTreeIcon sx={{ fontSize: 20, color: "#fff" }} />
					</Box>
					<Box>
						<Typography fontSize={16} fontWeight={700} color="#fff">
							Rearrange Agent Hierarchy
						</Typography>
						<Typography fontSize={12} color="rgba(255,255,255,0.7)">
							Drag agents between levels to reorganize — {currentAgent?.name ?? ""}
						</Typography>
					</Box>
				</Stack>
				<Stack direction="row" alignItems="center" spacing={1}>
					{hasChanges && (
						<Tooltip title="Undo last move">
							<IconButton
								onClick={handleUndo}
								disabled={isBusy}
								size="small"
								sx={{
									color: "rgba(255,255,255,0.8)",
									backgroundColor: "transparent !important",
									...generateButtonSx({ bg: "#F4F4F5 !important", text: "#1D54A2" }, { bg: "#F4F4F5 !important", text: "#1D54A2" }),
								}}
							>
								<UndoIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					<IconButton
						onClick={onClose}
						disabled={isBusy}
						size="small"
						sx={{
							color: "rgba(255,255,255,0.7)",
							backgroundColor: "transparent !important",
							...generateButtonSx({ bg: "#F4F4F5 !important", text: "#1D54A2" }, { bg: "#F4F4F5 !important", text: "#1D54A2" }),
						}}
					>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
				{/* Instruction banner */}
				<Box sx={{ px: 3, pt: 2, pb: 1 }}>
					<Alert
						severity="info"
						icon={<InfoOutlinedIcon fontSize="small" />}
						sx={{
							fontSize: 12,
							py: 0.5,
							borderRadius: "8px",
							bgcolor: "#E3F2FD",
							color: "#1565C0",
							border: "1px solid #BBDEFB",
							"& .MuiAlert-icon": { color: "#1976D2", alignItems: "center" },
						}}
					>
						Drag agent cards between columns to reassign their levels. For L2/L3 agents, select their parent from the dropdown. Changes
						are previewed below.
					</Alert>
				</Box>

				{/* Validation feedback */}
				{errors.length > 0 && (
					<Box sx={{ px: 3, pb: 1 }}>
						<Alert severity="error" sx={{ fontSize: 12, py: 0.5, borderRadius: "8px" }}>
							{errors.map((e, i) => (
								<div key={i}>{e}</div>
							))}
						</Alert>
					</Box>
				)}
				{warnings.length > 0 && (
					<Box sx={{ px: 3, pb: 1 }}>
						<Alert severity="warning" sx={{ fontSize: 12, py: 0.5, borderRadius: "8px" }}>
							{warnings.map((w, i) => (
								<div key={i}>{w}</div>
							))}
						</Alert>
					</Box>
				)}

				{/* Main board */}
				<Box sx={{ px: 3, pb: 2, flex: 1, overflow: "auto" }}>
					<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="stretch" sx={{ minHeight: 400 }}>
						<DropZone
							level="L1"
							agents={hierarchy.l1}
							onDrop={handleDrop}
							onRemoveFromZone={(id) => handleRemove(id, "L1")}
							parentOptions={[]}
							onParentChange={() => {}}
							disabled={isBusy}
						/>
						<DropZone
							level="L2"
							agents={hierarchy.l2}
							onDrop={handleDrop}
							onRemoveFromZone={(id) => handleRemove(id, "L2")}
							parentOptions={hierarchy.l1}
							onParentChange={(id, parentId) => handleParentChange(id, parentId, "L2")}
							disabled={isBusy}
						/>
						<DropZone
							level="L3"
							agents={hierarchy.l3}
							onDrop={handleDrop}
							onRemoveFromZone={(id) => handleRemove(id, "L3")}
							parentOptions={hierarchy.l2}
							onParentChange={(id, parentId) => handleParentChange(id, parentId, "L3")}
							disabled={isBusy}
						/>
						<DropZone
							level="UNASSIGNED"
							agents={hierarchy.unassigned}
							onDrop={handleDrop}
							onRemoveFromZone={() => {}}
							parentOptions={[]}
							onParentChange={() => {}}
							disabled={isBusy}
						/>
					</Stack>
				</Box>

				{/* Change log */}
				{hasChanges && (
					<>
						<Divider />
						<Box sx={{ px: 3, py: 1.5, bgcolor: "#fff" }}>
							<Typography fontSize={11} fontWeight={700} color="text.secondary" letterSpacing={0.5} mb={0.5}>
								PENDING CHANGES ({changeLog.length})
							</Typography>
							<Stack spacing={0} divider={<Divider />} sx={{ maxHeight: 110, overflowY: "auto" }}>
								{changeLog.map((change, i) => (
									<ChangeRow key={i} change={change} />
								))}
							</Stack>
						</Box>
					</>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E0E0E0", bgcolor: "#fff", gap: 1 }}>
				<Stack direction="row" alignItems="center" spacing={1} flex={1}>
					{hasChanges && (
						<Chip
							label={`${changeLog.length} change${changeLog.length !== 1 ? "s" : ""} pending`}
							size="small"
							sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 600, fontSize: 11 }}
						/>
					)}
				</Stack>
				<Button
					onClick={onClose}
					variant="outlined"
					size="small"
					disabled={isBusy}
					sx={{
						borderColor: "#CFD8DC",
						color: "text.secondary",
						backgroundColor: "transparent !important",
						...generateButtonSx({ bg: "#F4F4F5 !important", text: "#1D54A2" }, { bg: "#F4F4F5 !important", text: "#1D54A2" }),
					}}
				>
					Cancel
				</Button>
				{hasChanges && (
					<Button
						onClick={handleUndo}
						variant="outlined"
						startIcon={<UndoIcon />}
						size="small"
						disabled={isBusy}
						sx={{
							borderColor: "#CFD8DC",
							color: "text.secondary",
							backgroundColor: "transparent !important",
							...generateButtonSx({ bg: "#F4F4F5 !important", text: "#1D54A2" }, { bg: "#F4F4F5 !important", text: "#1D54A2" }),
						}}
					>
						Undo
					</Button>
				)}
				<Button
					onClick={handleSave}
					variant="contained"
					size="small"
					disabled={!hasChanges || !valid || isBusy}
					startIcon={isBusy ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : saved ? <CheckCircleOutlineIcon /> : null}
					sx={{
						backgroundColor: saved ? "#2E7D32 !important" : "#1D54A2 !important",
						px: 2.5,
						transition: "background-color 0.3s",
						color: saved ? "#fff !important" : "#F4F4F5 !important",
						...generateButtonSx({ bg: "#EBF5FF !important", text: "#1D54A2" }, { bg: "#EBF5FF !important", text: "#1D54A2" }),
					}}
				>
					{isBusy ? "Saving..." : saved ? "Saved!" : `Save Changes${hasChanges ? ` (${changeLog.length})` : ""}`}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
